"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DpCarousel = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var classnames_1 = __importDefault(require("classnames"));
var inRange_1 = __importDefault(require("lodash/inRange"));
var isNumber_1 = __importDefault(require("lodash/isNumber"));
var DpCarousel_styled_1 = require("./DpCarousel.styled");
var DpCarouselControl_1 = __importDefault(require("./DpCarouselControl"));
var api_1 = require("../../../api");
/**
 * DpCarousel
 * @property  {boolean}     multiple           - Indicates if it's a carousel with multiple visible items
 * @property  {Number}      maxItems           - Maximum number of visible items at a time
 * @property  {Array}       children           - List of elements to show
 * @property  {Function}    navigationHandler  - Optional callback function fired when the DpCarouselControl buttons are clicked
 * @property  {Number}      showIndex          - Index of the current item to be shown
 */
var DpCarousel = function (_a) {
    var _b;
    var children = _a.children, navigationHandler = _a.navigationHandler, _c = _a.maxItems, maxItems = _c === void 0 ? 1 : _c, _d = _a.multiple, multiple = _d === void 0 ? false : _d, _e = _a.controlPosition, controlPosition = _e === void 0 ? 'around' : _e, _f = _a.nextControlProps, nextControlProps = _f === void 0 ? { useLabel: true } : _f, _g = _a.prevControlProps, prevControlProps = _g === void 0 ? { useLabel: true } : _g, customClass = _a.customClass, showIndex = _a.showIndex, id = _a.id, ariaLabelText = _a.ariaLabelText, _h = _a.isResponsive, isResponsive = _h === void 0 ? false : _h;
    // ------------------------------- State
    var _j = (0, react_1.useState)(children.length - 1), lastIndex = _j[0], setLastIndex = _j[1];
    var _k = (0, react_1.useState)(0), activeIndex = _k[0], setActiveIndex = _k[1];
    var _l = (0, react_1.useState)(1), responsiveMaxItemsLimit = _l[0], setResponsiveMaxItemsLimit = _l[1];
    var _m = (0, react_1.useState)(false), callCarouselResponsiveOnce = _m[0], setCallCarouselResponsiveOnce = _m[1];
    var contentRef = (0, react_1.useRef)(null);
    /**
     * Update status when a new list of elements is provided
     */
    (0, react_1.useEffect)(function () {
        var _a;
        setLastIndex(children.length - 1);
        setActiveIndex(0);
        /**
         * React starts to call useMount before rendering, move setCarouselResponsive
         * to child.length to add the responsive function and added once.
         */
        if (isResponsive &&
            !callCarouselResponsiveOnce &&
            ((_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByClassName('dp-carousel-item current')[0])) {
            setCallCarouselResponsiveOnce(true);
            setCarouselResponsive();
        }
    }, [children.length, callCarouselResponsiveOnce, isResponsive]);
    (0, react_1.useEffect)(function () {
        if ((0, isNumber_1.default)(showIndex) && (0, inRange_1.default)(showIndex, 0, lastIndex + 1)) {
            setActiveIndex(showIndex);
        }
    }, [lastIndex, showIndex]);
    (0, api_1.useMount)(function () {
        if (isResponsive) {
            window.addEventListener('resize', setCarouselResponsive);
            return function () {
                window.removeEventListener('resize', setCarouselResponsive);
            };
        }
    });
    // ------------------------------- Actions
    var handleNavigate = function (newIndex) { return function () {
        setActiveIndex(newIndex);
        if (navigationHandler) {
            navigationHandler(newIndex);
        }
    }; };
    var setCarouselResponsive = function () {
        var _a, _b, _c;
        var limit = Math.floor((((_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) - 35 - 35) /
            ((_c = (_b = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _b === void 0 ? void 0 : _b.getElementsByClassName('dp-carousel-item current')[0]) === null || _c === void 0 ? void 0 : _c.clientWidth));
        setResponsiveMaxItemsLimit(limit);
    };
    // ------------------------------- Methods
    var isCurrentIndex = function (index) {
        return multiple
            ? (0, inRange_1.default)(index, activeIndex, activeIndex + (isResponsive ? responsiveMaxItemsLimit : maxItems))
            : index === activeIndex;
    };
    var showForwardNav = function () {
        return multiple
            ? activeIndex < children.length - (isResponsive ? responsiveMaxItemsLimit : maxItems)
            : activeIndex !== lastIndex;
    };
    // ------------------------------- Render
    var multipleClass = (0, classnames_1.default)({ multiple: multiple });
    var carouselContainerClasses = (0, classnames_1.default)('carousel-container', (_b = {},
        _b[controlPosition] = multiple && (isResponsive ? responsiveMaxItemsLimit : maxItems) < children.length,
        _b));
    return ((0, jsx_runtime_1.jsx)(DpCarousel_styled_1.StyledCarousel, { className: multipleClass, "data-testid": "carousel", children: (0, jsx_runtime_1.jsxs)("div", { className: carouselContainerClasses, ref: contentRef, children: [activeIndex !== 0 && ((0, jsx_runtime_1.jsx)("div", { className: "carousel-nav-".concat(controlPosition, " prev"), children: (0, jsx_runtime_1.jsx)(DpCarouselControl_1.default, __assign({ label: "previous", id: id, navigateHandler: handleNavigate(activeIndex - 1), customClass: customClass, cardNumber: activeIndex + 1, total: children.length, ariaLabelName: ariaLabelText }, prevControlProps, { controlPosition: controlPosition })) })), (0, jsx_runtime_1.jsx)("div", { className: "carousel-content", children: children.map(function (item, index) {
                        var current = isCurrentIndex(index);
                        var carouselClasses = (0, classnames_1.default)('dp-carousel-item', { current: current });
                        return ((0, jsx_runtime_1.jsx)("div", { className: carouselClasses, children: item }, item.key));
                    }) }), showForwardNav() && ((0, jsx_runtime_1.jsx)("div", { className: "carousel-nav-".concat(controlPosition, " next"), children: (0, jsx_runtime_1.jsx)(DpCarouselControl_1.default, __assign({ label: "next", id: id, controlPosition: controlPosition, navigateHandler: handleNavigate(activeIndex + 1), customClass: customClass, cardNumber: activeIndex + 1, total: children.length, ariaLabelName: ariaLabelText }, nextControlProps)) }))] }) }));
};
exports.DpCarousel = DpCarousel;
exports.default = exports.DpCarousel;
